// tasks.ts - Task management service for tasks, routines, todos, and visual schedules

import { supabase } from './supabase';
import type {
  Task,
  TaskAssignment,
  TaskType,
  ScheduleCategory,
  TaskDifficulty,
  ScheduleTemplate,
  ScheduleTemplateItem,
  TaskList,
  ListItem,
  Achievement,
  MemberAchievement,
  StreakHistory,
  LevelThreshold,
  FamilyMember,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface TaskFilters {
  taskType?: TaskType | TaskType[];
  scheduleCategory?: ScheduleCategory;
  assignedTo?: string;
  difficulty?: TaskDifficulty;
  minAge?: number;
  maxAge?: number;
  listId?: string;
  isActive?: boolean;
}

export interface AssignmentFilters {
  startDate?: string;
  endDate?: string;
  assignedTo?: string;
  templateId?: string;
  isCompleted?: boolean;
  includeSkipped?: boolean;
}

export interface CreateTaskData extends Partial<Task> {
  name: string;
  household_id: string;
}

export interface CreateAssignmentData {
  household_id: string;
  task_id: string;
  assigned_to: string;
  due_date: string;
  due_time?: string;
  scheduled_time?: string;
  notes?: string;
  assigned_by?: string;
  template_id?: string;
  template_item_id?: string;
}

export interface XPResult {
  new_total_xp: number;
  new_level: number;
  leveled_up: boolean;
}

export interface StreakResult {
  new_streak: number;
  streak_broken: boolean;
  used_freeze: boolean;
}

// ============================================
// TASK CRUD
// ============================================

/**
 * Get all tasks for a household with optional filtering
 */
export async function getTasks(
  householdId: string,
  filters: TaskFilters = {}
): Promise<Task[]> {
  let query = supabase
    .from('tasks')
    .select('*, list:task_lists(*)')
    .eq('household_id', householdId)
    .order('display_order')
    .order('sort_order')
    .order('name');

  // Apply task type filter
  if (filters.taskType) {
    if (Array.isArray(filters.taskType)) {
      query = query.in('task_type', filters.taskType);
    } else {
      query = query.eq('task_type', filters.taskType);
    }
  }

  // Apply schedule category filter
  if (filters.scheduleCategory) {
    query = query.eq('schedule_category', filters.scheduleCategory);
  }

  // Apply difficulty filter
  if (filters.difficulty) {
    query = query.eq('difficulty', filters.difficulty);
  }

  // Apply list filter
  if (filters.listId) {
    query = query.eq('list_id', filters.listId);
  }

  // Apply active filter (default to true)
  const isActive = filters.isActive ?? true;
  query = query.eq('is_active', isActive);

  // Apply age filters
  if (filters.minAge !== undefined) {
    query = query.or(`min_age.is.null,min_age.lte.${filters.minAge}`);
  }
  if (filters.maxAge !== undefined) {
    query = query.or(`max_age.is.null,max_age.gte.${filters.maxAge}`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching tasks:', error);
    return [];
  }

  return data || [];
}

/**
 * Get a single task by ID
 */
export async function getTask(taskId: string): Promise<Task | null> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*, list:task_lists(*)')
    .eq('id', taskId)
    .single();

  if (error) {
    console.error('Error fetching task:', error);
    return null;
  }

  return data;
}

/**
 * Get tasks suitable for a specific age
 */
export async function getAgeAppropriateTasks(
  householdId: string,
  age: number
): Promise<Task[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('household_id', householdId)
    .eq('is_active', true)
    .or(`min_age.is.null,min_age.lte.${age}`)
    .or(`max_age.is.null,max_age.gte.${age}`)
    .order('difficulty')
    .order('name');

  if (error) {
    console.error('Error fetching age-appropriate tasks:', error);
    return [];
  }

  return data || [];
}

/**
 * Create a new task
 */
export async function createTask(data: CreateTaskData): Promise<Task | null> {
  const { data: result, error } = await supabase
    .from('tasks')
    .insert({
      ...data,
      task_type: data.task_type || 'chore',
      difficulty: data.difficulty || 'medium',
      is_active: true,
    })
    .select('*, list:task_lists(*)')
    .single();

  if (error) {
    console.error('Error creating task:', error);
    return null;
  }

  return result;
}

/**
 * Update a task
 */
export async function updateTask(
  taskId: string,
  updates: Partial<Task>
): Promise<Task | null> {
  const { data, error } = await supabase
    .from('tasks')
    .update(updates)
    .eq('id', taskId)
    .select('*, list:task_lists(*)')
    .single();

  if (error) {
    console.error('Error updating task:', error);
    return null;
  }

  return data;
}

/**
 * Soft delete a task (set is_active to false)
 */
export async function deleteTask(taskId: string): Promise<boolean> {
  const { error } = await supabase
    .from('tasks')
    .update({ is_active: false })
    .eq('id', taskId);

  if (error) {
    console.error('Error deleting task:', error);
    return false;
  }

  return true;
}

/**
 * Hard delete a task (permanent)
 */
export async function permanentlyDeleteTask(taskId: string): Promise<boolean> {
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('id', taskId);

  if (error) {
    console.error('Error permanently deleting task:', error);
    return false;
  }

  return true;
}

// ============================================
// ASSIGNMENT OPERATIONS
// ============================================

/**
 * Get assignments with filtering
 */
export async function getAssignments(
  householdId: string,
  filters: AssignmentFilters = {}
): Promise<TaskAssignment[]> {
  let query = supabase
    .from('task_assignments')
    .select(`
      *,
      task:tasks(*),
      assignee:family_members!assigned_to(*),
      template:schedule_templates(*),
      template_item:schedule_template_items(*)
    `)
    .eq('household_id', householdId)
    .order('due_date')
    .order('scheduled_time', { nullsFirst: false })
    .order('due_time', { nullsFirst: false });

  // Date range filter
  if (filters.startDate) {
    query = query.gte('due_date', filters.startDate);
  }
  if (filters.endDate) {
    query = query.lte('due_date', filters.endDate);
  }

  // Assigned to filter
  if (filters.assignedTo) {
    query = query.eq('assigned_to', filters.assignedTo);
  }

  // Template filter
  if (filters.templateId) {
    query = query.eq('template_id', filters.templateId);
  }

  // Completion filter
  if (filters.isCompleted !== undefined) {
    query = query.eq('is_completed', filters.isCompleted);
  }

  // Skip filter - exclude skipped unless explicitly included
  if (!filters.includeSkipped) {
    query = query.or('skipped.is.null,skipped.eq.false');
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching assignments:', error);
    return [];
  }

  return data || [];
}

/**
 * Get today's assignments for a member (for visual schedule display)
 */
export async function getTodaysAssignments(
  householdId: string,
  memberId?: string
): Promise<TaskAssignment[]> {
  const today = new Date().toISOString().split('T')[0];

  return getAssignments(householdId, {
    startDate: today,
    endDate: today,
    assignedTo: memberId,
    includeSkipped: false,
  });
}

/**
 * Create a new assignment
 */
export async function createAssignment(
  data: CreateAssignmentData
): Promise<TaskAssignment | null> {
  const { data: result, error } = await supabase
    .from('task_assignments')
    .insert(data)
    .select(`
      *,
      task:tasks(*),
      assignee:family_members!assigned_to(*)
    `)
    .single();

  if (error) {
    console.error('Error creating assignment:', error);
    return null;
  }

  return result;
}

/**
 * Complete an assignment (with XP and streak handling)
 */
export async function completeAssignment(
  assignmentId: string,
  completedBy: string,
  options: { addXp?: boolean; updateStreak?: boolean } = {}
): Promise<{ assignment: TaskAssignment | null; xpResult?: XPResult }> {
  const { addXp = true } = options;

  // Get the assignment with task details
  const { data: assignment, error: fetchError } = await supabase
    .from('task_assignments')
    .select('*, task:tasks(*)')
    .eq('id', assignmentId)
    .single();

  if (fetchError || !assignment) {
    console.error('Error fetching assignment:', fetchError);
    return { assignment: null };
  }

  // Update the assignment
  const now = new Date().toISOString();
  const { data: updated, error: updateError } = await supabase
    .from('task_assignments')
    .update({
      is_completed: true,
      completed_at: now,
      completed_by: completedBy,
      actual_end_time: now,
    })
    .eq('id', assignmentId)
    .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
    .single();

  if (updateError) {
    console.error('Error completing assignment:', updateError);
    return { assignment: null };
  }

  let xpResult: XPResult | undefined;

  // Award XP if enabled and task has XP value
  if (addXp && assignment.assigned_to && assignment.task?.xp_reward) {
    const result = await addXP(assignment.assigned_to, assignment.task.xp_reward);
    xpResult = result ?? undefined;
  }

  // Update points if task has point value
  if (assignment.assigned_to && assignment.task?.points_value) {
    await supabase.rpc('increment_points', {
      member_id: assignment.assigned_to,
      points_delta: assignment.task.points_value,
    });
  }

  return { assignment: updated, xpResult };
}

/**
 * Uncomplete an assignment (revert completion)
 */
export async function uncompleteAssignment(
  assignmentId: string
): Promise<TaskAssignment | null> {
  // Get the assignment first to check for XP/points to deduct
  const { data: assignment } = await supabase
    .from('task_assignments')
    .select('*, task:tasks(*)')
    .eq('id', assignmentId)
    .single();

  if (!assignment) return null;

  // Revert the assignment
  const { data: updated, error } = await supabase
    .from('task_assignments')
    .update({
      is_completed: false,
      completed_at: null,
      completed_by: null,
      actual_end_time: null,
    })
    .eq('id', assignmentId)
    .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
    .single();

  if (error) {
    console.error('Error uncompleting assignment:', error);
    return null;
  }

  // Deduct points if they were awarded
  if (assignment.is_completed && assignment.assigned_to && assignment.task?.points_value) {
    await supabase.rpc('increment_points', {
      member_id: assignment.assigned_to,
      points_delta: -assignment.task.points_value,
    });
  }

  // Note: We don't deduct XP as that would be frustrating for users
  // XP represents total effort, not current status

  return updated;
}

/**
 * Skip an assignment
 */
export async function skipAssignment(
  assignmentId: string,
  reason?: string
): Promise<TaskAssignment | null> {
  const { data, error } = await supabase
    .from('task_assignments')
    .update({
      skipped: true,
      skip_reason: reason,
    })
    .eq('id', assignmentId)
    .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
    .single();

  if (error) {
    console.error('Error skipping assignment:', error);
    return null;
  }

  return data;
}

/**
 * Start working on an assignment (set actual_start_time)
 */
export async function startAssignment(
  assignmentId: string
): Promise<TaskAssignment | null> {
  const { data, error } = await supabase
    .from('task_assignments')
    .update({
      actual_start_time: new Date().toISOString(),
    })
    .eq('id', assignmentId)
    .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
    .single();

  if (error) {
    console.error('Error starting assignment:', error);
    return null;
  }

  return data;
}

/**
 * Delete an assignment
 */
export async function deleteAssignment(assignmentId: string): Promise<boolean> {
  const { error } = await supabase
    .from('task_assignments')
    .delete()
    .eq('id', assignmentId);

  if (error) {
    console.error('Error deleting assignment:', error);
    return false;
  }

  return true;
}

// ============================================
// SCHEDULE TEMPLATES
// ============================================

/**
 * Get all schedule templates for a household
 */
export async function getScheduleTemplates(
  householdId: string,
  memberId?: string
): Promise<ScheduleTemplate[]> {
  let query = supabase
    .from('schedule_templates')
    .select(`
      *,
      assignee:family_members!assigned_to(*),
      items:schedule_template_items(*, task:tasks(*))
    `)
    .eq('household_id', householdId)
    .eq('is_active', true)
    .order('schedule_category')
    .order('name');

  if (memberId) {
    // Filter by either assigned_to (legacy) or assigned_to_ids (new)
    query = query.or(`assigned_to.eq.${memberId},assigned_to_ids.cs.{${memberId}}`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching schedule templates:', error);
    return [];
  }

  // Collect all unique member IDs from assigned_to_ids
  const allMemberIds = new Set<string>();
  (data || []).forEach(template => {
    if (template.assigned_to_ids && Array.isArray(template.assigned_to_ids)) {
      template.assigned_to_ids.forEach((id: string) => allMemberIds.add(id));
    }
  });

  // Fetch all family members for assigned_to_ids if any
  let membersMap: Record<string, FamilyMember> = {};
  if (allMemberIds.size > 0) {
    const { data: members } = await supabase
      .from('family_members')
      .select('*')
      .in('id', Array.from(allMemberIds));

    if (members) {
      membersMap = members.reduce((acc, m) => {
        acc[m.id] = m;
        return acc;
      }, {} as Record<string, FamilyMember>);
    }
  }

  // Sort items and map assignees
  return (data || []).map(template => {
    // Build assignees array from assigned_to_ids
    const assignees: FamilyMember[] = [];
    if (template.assigned_to_ids && Array.isArray(template.assigned_to_ids)) {
      for (const id of template.assigned_to_ids) {
        if (membersMap[id]) {
          assignees.push(membersMap[id]);
        }
      }
    } else if (template.assignee) {
      // Fallback to legacy single assignee
      assignees.push(template.assignee);
    }

    return {
      ...template,
      assignees,
      items: (template.items || []).sort(
        (a: ScheduleTemplateItem, b: ScheduleTemplateItem) =>
          (a.display_order || 0) - (b.display_order || 0)
      ),
    };
  });
}

/**
 * Get a single schedule template with items
 */
export async function getScheduleTemplate(
  templateId: string
): Promise<ScheduleTemplate | null> {
  const { data, error } = await supabase
    .from('schedule_templates')
    .select(`
      *,
      assigned_member:family_members!assigned_to(*),
      items:schedule_template_items(*, task:tasks(*))
    `)
    .eq('id', templateId)
    .single();

  if (error) {
    console.error('Error fetching schedule template:', error);
    return null;
  }

  // Sort items by display_order
  if (data?.items) {
    data.items = data.items.sort(
      (a: ScheduleTemplateItem, b: ScheduleTemplateItem) =>
        (a.display_order || 0) - (b.display_order || 0)
    );
  }

  return data;
}

/**
 * Create a schedule template
 */
export async function createScheduleTemplate(
  data: Partial<ScheduleTemplate> & { household_id: string; name: string; schedule_category: string }
): Promise<ScheduleTemplate | null> {
  const { data: result, error } = await supabase
    .from('schedule_templates')
    .insert({
      ...data,
      is_active: true,
    })
    .select('*')
    .single();

  if (error) {
    console.error('Error creating schedule template:', error);
    return null;
  }

  return result;
}

/**
 * Update a schedule template
 */
export async function updateScheduleTemplate(
  templateId: string,
  updates: Partial<ScheduleTemplate>
): Promise<ScheduleTemplate | null> {
  const { data, error } = await supabase
    .from('schedule_templates')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', templateId)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating schedule template:', error);
    return null;
  }

  return data;
}

/**
 * Delete a schedule template
 */
export async function deleteScheduleTemplate(
  templateId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('schedule_templates')
    .update({ is_active: false })
    .eq('id', templateId);

  if (error) {
    console.error('Error deleting schedule template:', error);
    return false;
  }

  return true;
}

/**
 * Add an item to a schedule template
 */
export async function addTemplateItem(
  templateId: string,
  taskId: string,
  options: Partial<ScheduleTemplateItem> = {}
): Promise<ScheduleTemplateItem | null> {
  // Get current max display_order
  const { data: existing } = await supabase
    .from('schedule_template_items')
    .select('display_order')
    .eq('template_id', templateId)
    .order('display_order', { ascending: false })
    .limit(1);

  const nextOrder = (existing?.[0]?.display_order || 0) + 1;

  const { data, error } = await supabase
    .from('schedule_template_items')
    .insert({
      template_id: templateId,
      task_id: taskId,
      display_order: options.display_order ?? nextOrder,
      scheduled_time: options.scheduled_time,
      duration_override: options.duration_override,
      notes: options.notes,
      is_optional: options.is_optional ?? false,
    })
    .select('*, task:tasks(*)')
    .single();

  if (error) {
    console.error('Error adding template item:', error);
    return null;
  }

  return data;
}

/**
 * Update a template item
 */
export async function updateTemplateItem(
  itemId: string,
  updates: Partial<ScheduleTemplateItem>
): Promise<ScheduleTemplateItem | null> {
  const { data, error } = await supabase
    .from('schedule_template_items')
    .update(updates)
    .eq('id', itemId)
    .select('*, task:tasks(*)')
    .single();

  if (error) {
    console.error('Error updating template item:', error);
    return null;
  }

  return data;
}

/**
 * Remove an item from a template
 */
export async function removeTemplateItem(itemId: string): Promise<boolean> {
  const { error } = await supabase
    .from('schedule_template_items')
    .delete()
    .eq('id', itemId);

  if (error) {
    console.error('Error removing template item:', error);
    return false;
  }

  return true;
}

/**
 * Reorder template items
 */
export async function reorderTemplateItems(
  updates: Array<{ id: string; display_order: number }>
): Promise<boolean> {
  const promises = updates.map(({ id, display_order }) =>
    supabase
      .from('schedule_template_items')
      .update({ display_order })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  return results.every(r => !r.error);
}

/**
 * Create assignments from a schedule template for a specific date
 * Supports multi-select: creates assignments for each member in assigned_to_ids
 * Prevents duplicates: won't create if assignments already exist for this template + date
 */
export async function createAssignmentsFromTemplate(
  templateId: string,
  date: string,
  assignedBy?: string
): Promise<TaskAssignment[]> {
  const template = await getScheduleTemplate(templateId);
  if (!template || !template.items?.length) {
    return [];
  }

  // Get assignees: prefer assigned_to_ids array, fall back to single assigned_to
  const assignees = template.assigned_to_ids?.length
    ? template.assigned_to_ids
    : template.assigned_to
      ? [template.assigned_to]
      : [];

  if (assignees.length === 0) {
    console.warn('Template has no assignees:', templateId);
    return [];
  }

  // Check for existing assignments from this template on this date
  // This prevents duplicates when clicking "Apply Today" multiple times
  const { data: existingAssignments } = await supabase
    .from('task_assignments')
    .select('id, assigned_to, template_item_id')
    .eq('template_id', templateId)
    .eq('due_date', date);

  // Build a Set of existing "memberId:templateItemId" combinations
  const existingKeys = new Set(
    (existingAssignments || []).map(a => `${a.assigned_to}:${a.template_item_id}`)
  );

  // Create assignments for each assignee, skipping existing ones
  const newAssignments: CreateAssignmentData[] = [];
  for (const memberId of assignees) {
    for (const item of template.items) {
      const key = `${memberId}:${item.id}`;
      if (!existingKeys.has(key)) {
        newAssignments.push({
          household_id: template.household_id,
          task_id: item.task_id,
          assigned_to: memberId,
          due_date: date,
          scheduled_time: item.scheduled_time || undefined,
          notes: item.notes || undefined,
          assigned_by: assignedBy,
          template_id: templateId,
          template_item_id: item.id,
        });
      }
    }
  }

  // If all assignments already exist, return existing ones
  if (newAssignments.length === 0) {
    console.log('All assignments already exist for this template and date');
    // Return existing assignments with full data
    const { data: fullExisting } = await supabase
      .from('task_assignments')
      .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
      .eq('template_id', templateId)
      .eq('due_date', date);
    return fullExisting || [];
  }

  const { data, error } = await supabase
    .from('task_assignments')
    .insert(newAssignments)
    .select('*, task:tasks(*), assignee:family_members!assigned_to(*)');

  if (error) {
    console.error('Error creating assignments from template:', error);
    return [];
  }

  // If some already existed, also fetch those to return complete set
  if (existingAssignments && existingAssignments.length > 0) {
    const { data: allAssignments } = await supabase
      .from('task_assignments')
      .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
      .eq('template_id', templateId)
      .eq('due_date', date);
    return allAssignments || [];
  }

  return data || [];
}

// ============================================
// TASK LISTS
// ============================================

/**
 * Get all task lists for a household
 */
export async function getTaskLists(
  householdId: string,
  options: { includeArchived?: boolean; listType?: string } = {}
): Promise<TaskList[]> {
  let query = supabase
    .from('task_lists')
    .select('*, owner:family_members!owner_id(*), list_items(count)')
    .eq('household_id', householdId)
    .order('is_pinned', { ascending: false })
    .order('sort_order')
    .order('name');

  if (!options.includeArchived) {
    query = query.eq('is_archived', false);
  }

  if (options.listType) {
    query = query.eq('list_type', options.listType);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching task lists:', error);
    return [];
  }

  // Map the count from the nested list_items array to item_count
  return (data || []).map(list => ({
    ...list,
    item_count: (list.list_items as any)?.[0]?.count || 0,
    list_items: undefined, // Remove the nested array
  }));
}

/**
 * Get a single task list with its tasks
 */
export async function getTaskListWithTasks(
  listId: string
): Promise<{ list: TaskList; tasks: Task[] } | null> {
  const { data: list, error: listError } = await supabase
    .from('task_lists')
    .select('*, owner:family_members!owner_id(*)')
    .eq('id', listId)
    .single();

  if (listError || !list) {
    console.error('Error fetching task list:', listError);
    return null;
  }

  const { data: tasks } = await supabase
    .from('tasks')
    .select('*')
    .eq('list_id', listId)
    .eq('is_active', true)
    .order('sort_order')
    .order('name');

  return { list, tasks: tasks || [] };
}

/**
 * Create a task list
 */
export async function createTaskList(
  data: Partial<TaskList> & { household_id: string; name: string }
): Promise<TaskList | null> {
  const { data: result, error } = await supabase
    .from('task_lists')
    .insert({
      ...data,
      list_type: data.list_type || 'todo',
      is_shared: data.is_shared ?? true,
    })
    .select('*, owner:family_members!owner_id(*)')
    .single();

  if (error) {
    console.error('Error creating task list:', error);
    return null;
  }

  return result;
}

/**
 * Update a task list
 */
export async function updateTaskList(
  listId: string,
  updates: Partial<TaskList>
): Promise<TaskList | null> {
  const { data, error } = await supabase
    .from('task_lists')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', listId)
    .select('*, owner:family_members!owner_id(*)')
    .single();

  if (error) {
    console.error('Error updating task list:', error);
    return null;
  }

  return data;
}

/**
 * Delete a task list (and optionally its tasks)
 */
export async function deleteTaskList(
  listId: string,
  deleteListTasks: boolean = false
): Promise<boolean> {
  if (deleteListTasks) {
    // Delete all tasks in the list
    await supabase
      .from('tasks')
      .delete()
      .eq('list_id', listId);
  } else {
    // Just remove the list reference from tasks
    await supabase
      .from('tasks')
      .update({ list_id: null })
      .eq('list_id', listId);
  }

  const { error } = await supabase
    .from('task_lists')
    .delete()
    .eq('id', listId);

  if (error) {
    console.error('Error deleting task list:', error);
    return false;
  }

  return true;
}

// ============================================
// GAMIFICATION
// ============================================

/**
 * Add XP to a member (uses database function for level calculation)
 */
export async function addXP(
  memberId: string,
  xpDelta: number
): Promise<XPResult | null> {
  const { data, error } = await supabase
    .rpc('add_xp', {
      p_member_id: memberId,
      p_xp_delta: xpDelta,
    });

  if (error) {
    console.error('Error adding XP:', error);
    return null;
  }

  // The RPC returns a single row
  return data?.[0] || null;
}

/**
 * Update streak for a member
 */
export async function updateStreak(
  memberId: string,
  date: string,
  wasPerfect: boolean
): Promise<StreakResult | null> {
  const { data, error } = await supabase
    .rpc('update_streak', {
      p_member_id: memberId,
      p_date: date,
      p_was_perfect: wasPerfect,
    });

  if (error) {
    console.error('Error updating streak:', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Get level thresholds
 */
export async function getLevelThresholds(): Promise<LevelThreshold[]> {
  const { data, error } = await supabase
    .from('level_thresholds')
    .select('*')
    .order('level');

  if (error) {
    console.error('Error fetching level thresholds:', error);
    return [];
  }

  return data || [];
}

/**
 * Get current level info for XP amount
 */
export async function getLevelInfo(xp: number): Promise<{
  current: LevelThreshold | null;
  next: LevelThreshold | null;
  progress: number;
}> {
  const thresholds = await getLevelThresholds();

  let current: LevelThreshold | null = null;
  let next: LevelThreshold | null = null;

  for (let i = 0; i < thresholds.length; i++) {
    if (xp >= thresholds[i].xp_required) {
      current = thresholds[i];
      next = thresholds[i + 1] || null;
    }
  }

  // Calculate progress to next level
  let progress = 100;
  if (current && next) {
    const levelXP = xp - current.xp_required;
    const neededXP = next.xp_required - current.xp_required;
    progress = Math.min(100, Math.round((levelXP / neededXP) * 100));
  }

  return { current, next, progress };
}

// ============================================
// ACHIEVEMENTS
// ============================================

/**
 * Get all available achievements
 */
export async function getAchievements(
  category?: string
): Promise<Achievement[]> {
  let query = supabase
    .from('achievements')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
    .order('name');

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching achievements:', error);
    return [];
  }

  return data || [];
}

/**
 * Get achievements earned by a member
 */
export async function getMemberAchievements(
  memberId: string
): Promise<MemberAchievement[]> {
  const { data, error } = await supabase
    .from('member_achievements')
    .select('*, achievement:achievements(*)')
    .eq('member_id', memberId)
    .order('earned_at', { ascending: false });

  if (error) {
    console.error('Error fetching member achievements:', error);
    return [];
  }

  return data || [];
}

/**
 * Award an achievement to a member
 */
export async function awardAchievement(
  memberId: string,
  achievementId: string
): Promise<MemberAchievement | null> {
  // Check if already earned
  const { data: existing } = await supabase
    .from('member_achievements')
    .select('id')
    .eq('member_id', memberId)
    .eq('achievement_id', achievementId)
    .single();

  if (existing) {
    return null; // Already earned
  }

  // Get achievement for XP/points reward
  const { data: achievement } = await supabase
    .from('achievements')
    .select('*')
    .eq('id', achievementId)
    .single();

  if (!achievement) {
    return null;
  }

  // Award the achievement
  const { data, error } = await supabase
    .from('member_achievements')
    .insert({
      member_id: memberId,
      achievement_id: achievementId,
    })
    .select('*, achievement:achievements(*)')
    .single();

  if (error) {
    console.error('Error awarding achievement:', error);
    return null;
  }

  // Award XP if applicable
  if (achievement.xp_reward > 0) {
    await addXP(memberId, achievement.xp_reward);
  }

  // Award points if applicable
  if (achievement.points_reward > 0) {
    await supabase.rpc('increment_points', {
      member_id: memberId,
      points_delta: achievement.points_reward,
    });
  }

  return data;
}

/**
 * Check and award achievements based on member stats
 * Call this after completing tasks, earning XP, etc.
 */
export async function checkAchievements(
  memberId: string,
  stats: {
    totalCompleted?: number;
    currentStreak?: number;
    totalXP?: number;
    level?: number;
  }
): Promise<MemberAchievement[]> {
  const achievements = await getAchievements();
  const earned = await getMemberAchievements(memberId);
  const earnedIds = new Set(earned.map(e => e.achievement_id));

  const newlyEarned: MemberAchievement[] = [];

  for (const achievement of achievements) {
    // Skip if already earned or secret
    if (earnedIds.has(achievement.id) || achievement.is_secret) {
      continue;
    }

    let qualifies = false;

    switch (achievement.requirement_type) {
      case 'total_completed':
        qualifies = (stats.totalCompleted || 0) >= (achievement.requirement_value || 0);
        break;
      case 'streak_days':
        qualifies = (stats.currentStreak || 0) >= (achievement.requirement_value || 0);
        break;
      case 'points_earned':
        // Points are separate from XP, would need to pass in separately
        break;
      case 'level_reached':
        qualifies = (stats.level || 1) >= (achievement.requirement_value || 0);
        break;
      case 'first_task':
        qualifies = (stats.totalCompleted || 0) >= 1;
        break;
    }

    if (qualifies) {
      const awarded = await awardAchievement(memberId, achievement.id);
      if (awarded) {
        newlyEarned.push(awarded);
      }
    }
  }

  return newlyEarned;
}

// ============================================
// STREAK HISTORY
// ============================================

/**
 * Get streak history for a member
 */
export async function getStreakHistory(
  memberId: string,
  limit: number = 30
): Promise<StreakHistory[]> {
  const { data, error } = await supabase
    .from('streak_history')
    .select('*')
    .eq('member_id', memberId)
    .order('date', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching streak history:', error);
    return [];
  }

  return data || [];
}

/**
 * Record daily stats for streak tracking
 */
export async function recordDailyStats(
  memberId: string,
  date: string,
  stats: Partial<StreakHistory>
): Promise<StreakHistory | null> {
  const { data, error } = await supabase
    .from('streak_history')
    .upsert({
      member_id: memberId,
      date,
      ...stats,
    }, {
      onConflict: 'member_id,date',
    })
    .select()
    .single();

  if (error) {
    console.error('Error recording daily stats:', error);
    return null;
  }

  return data;
}

// ============================================
// GAMIFICATION STATS
// ============================================

/**
 * Get gamification stats for a member
 */
export async function getGamificationStats(
  memberId: string
): Promise<{
  member: FamilyMember | null;
  levelInfo: { current: LevelThreshold | null; next: LevelThreshold | null; progress: number };
  achievements: MemberAchievement[];
  recentHistory: StreakHistory[];
}> {
  // Get member with gamification fields
  const { data: member } = await supabase
    .from('family_members')
    .select('*')
    .eq('id', memberId)
    .single();

  // Get level info
  const levelInfo = await getLevelInfo(member?.total_xp || 0);

  // Get achievements
  const achievements = await getMemberAchievements(memberId);

  // Get recent streak history
  const recentHistory = await getStreakHistory(memberId, 7);

  return {
    member,
    levelInfo,
    achievements,
    recentHistory,
  };
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to task changes
 */
export function subscribeToTasks(
  householdId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`tasks-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tasks',
        filter: `household_id=eq.${householdId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to assignment changes
 */
export function subscribeToAssignments(
  householdId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`assignments-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'task_assignments',
        filter: `household_id=eq.${householdId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to achievement changes for a member
 */
export function subscribeToAchievements(
  memberId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`achievements-${memberId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'member_achievements',
        filter: `member_id=eq.${memberId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to schedule template changes
 */
export function subscribeToScheduleTemplates(
  householdId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`templates-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'schedule_templates',
        filter: `household_id=eq.${householdId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================
// LIST ITEMS CRUD
// ============================================

export interface CreateListItemData {
  list_id: string;
  household_id: string;
  name: string;
  quantity?: string;
  unit?: string;
  category?: string;
  notes?: string;
  added_by?: string;
}

/**
 * Get all items for a list
 */
export async function getListItems(
  listId: string,
  options: { includeChecked?: boolean } = {}
): Promise<ListItem[]> {
  const query = supabase
    .from('list_items')
    .select('*, checked_by_member:family_members!checked_by(*), added_by_member:family_members!added_by(*)')
    .eq('list_id', listId)
    .order('is_checked')
    .order('category')
    .order('sort_order');

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching list items:', error);
    return [];
  }

  return data || [];
}

/**
 * Create a new list item
 */
export async function createListItem(
  data: CreateListItemData
): Promise<ListItem | null> {
  // Get max sort_order for this list
  const { data: maxItem } = await supabase
    .from('list_items')
    .select('sort_order')
    .eq('list_id', data.list_id)
    .order('sort_order', { ascending: false })
    .limit(1)
    .single();

  const { data: newItem, error } = await supabase
    .from('list_items')
    .insert({
      ...data,
      sort_order: (maxItem?.sort_order || 0) + 1,
    })
    .select('*, checked_by_member:family_members!checked_by(*), added_by_member:family_members!added_by(*)')
    .single();

  if (error) {
    console.error('Error creating list item:', error);
    return null;
  }

  return newItem;
}

/**
 * Update a list item
 */
export async function updateListItem(
  itemId: string,
  updates: Partial<ListItem>
): Promise<ListItem | null> {
  const { data, error } = await supabase
    .from('list_items')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', itemId)
    .select('*, checked_by_member:family_members!checked_by(*), added_by_member:family_members!added_by(*)')
    .single();

  if (error) {
    console.error('Error updating list item:', error);
    return null;
  }

  return data;
}

/**
 * Check/uncheck a list item
 */
export async function checkListItem(
  itemId: string,
  isChecked: boolean,
  checkedBy?: string
): Promise<ListItem | null> {
  const { data, error } = await supabase
    .from('list_items')
    .update({
      is_checked: isChecked,
      checked_by: isChecked ? checkedBy : null,
      checked_at: isChecked ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', itemId)
    .select('*, checked_by_member:family_members!checked_by(*), added_by_member:family_members!added_by(*)')
    .single();

  if (error) {
    console.error('Error checking list item:', error);
    return null;
  }

  return data;
}

/**
 * Delete a list item
 */
export async function deleteListItem(itemId: string): Promise<boolean> {
  const { error } = await supabase
    .from('list_items')
    .delete()
    .eq('id', itemId);

  if (error) {
    console.error('Error deleting list item:', error);
    return false;
  }

  return true;
}

/**
 * Clear all checked items from a list
 */
export async function clearCheckedItems(listId: string): Promise<boolean> {
  const { error } = await supabase
    .from('list_items')
    .delete()
    .eq('list_id', listId)
    .eq('is_checked', true);

  if (error) {
    console.error('Error clearing checked items:', error);
    return false;
  }

  return true;
}

/**
 * Reorder list items
 */
export async function reorderListItems(
  updates: Array<{ id: string; sort_order: number }>
): Promise<boolean> {
  const promises = updates.map(({ id, sort_order }) =>
    supabase
      .from('list_items')
      .update({ sort_order })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  const hasError = results.some(r => r.error);

  if (hasError) {
    console.error('Error reordering list items');
    return false;
  }

  return true;
}

/**
 * Subscribe to list item changes
 */
export function subscribeToListItems(
  listId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`list-items-${listId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'list_items',
        filter: `list_id=eq.${listId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to task list changes
 */
export function subscribeToTaskLists(
  householdId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`task-lists-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'task_lists',
        filter: `household_id=eq.${householdId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
